import { getFirstChar } from '../utils/string';
import * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { inview } from '../utils/lazy';

export type AdElement = {
    height: number;
    width: number;
    adType: string;
    container: {
        id: string;
        className: string;
    };
};
type AdCache = {
    elements: {
        [id: string]: AdElement;
    };
    url: string;
};

const emptyObject: AdCache = {
    elements: {},
    url: '',
};

const AD_CACHE_KEY = 'ad-cache';

export const useCachedAdPlaceholder = (
    id: string,
    targetId: string,
    pageType: string,
    status: string,
    doNotUsePlaceholder: boolean,
): {
    loadCachedFallbackAd: boolean;
    adCache: AdElement | null;
    CachedPlaceholderElement: React.ReactElement | null;
} => {
    const [isInView, setIsInView] = React.useState(false);
    const [adCache, setAdCache] = React.useState<AdElement | null>(
        getCachedPlaceholderAd(id),
    );
    const cleanup = useRef(() => {});

    useEffect(() => {
        if (id.includes('board_')) {
            setAdCache(getCachedPlaceholderAd(id));
        }
    }, [id]);

    const loadCachedFallbackAd = useMemo(() => {
        return (
            !isInView &&
            id.includes('board_') &&
            adCache &&
            pageType === 'result' &&
            status !== undefined &&
            status !== 'loaded'
        );
    }, [id, adCache, pageType, status, isInView]);

    function triggerInView() {
        setIsInView(true);
    }

    useEffect(() => {
        if (loadCachedFallbackAd && adCache?.container?.id) {
            const element = document.getElementById(
                adCache.container.id + '-cached-ad-placeholder',
            );
            if (element) {
                cleanup.current = inview(element, triggerInView, 100);
            }
        }

        return cleanup.current;
    }, [id, adCache?.container?.id, loadCachedFallbackAd]);

    const CachedPlaceholderElement = !adCache ? null : (
        <div
            id={adCache?.container?.id + '-cached-ad-placeholder'}
            className={adCache?.container?.className}
            style={{
                height: adCache?.height,
            }}
        >
            <div
                id={targetId}
                style={{
                    height: adCache?.height,
                }}
            />
        </div>
    );

    if (doNotUsePlaceholder)
        return {
            loadCachedFallbackAd: false,
            adCache: null,
            CachedPlaceholderElement: null,
        };

    return {
        loadCachedFallbackAd,
        CachedPlaceholderElement,
        adCache,
    };
};

function getAdCache(): AdCache {
    if (window) {
        return JSON.parse(sessionStorage.getItem(AD_CACHE_KEY)) || emptyObject;
    }
}

function getCachedPlaceholderAd(id): AdElement | null {
    const currentAdCache = getAdCache();
    if (checkToClear(currentAdCache)) {
        clearAdPlaceholderCache();
        return null;
    } else {
        if (currentAdCache.elements[id]) {
            return currentAdCache.elements[id];
        } else return null;
    }
}

function cleanIdWithPaginationNumbers(id: string): string {
    const numbers = id.replace(/\D/g, '');
    const cleanId = id.replace(/\d/g, '');
    return `${cleanId}${getFirstChar(numbers)}`;
}

function getUrlWithoutHash() {
    return window ? window.location.href.split('#')[0] : '';
}

export function cachePlaceholderAd(
    id: string,
    element: HTMLElement,
    adType: string,
): void {
    if (window) {
        const currentAdCache: AdCache = getAdCache();
        if (element) {
            const isNative = adType === 'native';
            const containerClassName = isNative
                ? element.className
                : element.parentElement?.className;
            const containerId = isNative
                ? element.id
                : element.parentElement?.id;

            if (containerId.includes('board_')) {
                if (element.offsetHeight === 0) {
                    return;
                }
                const adCache: AdElement = {
                    adType: adType,
                    height: element.offsetHeight,
                    width: element.offsetWidth,
                    container: {
                        id: containerId,
                        className: containerClassName,
                    },
                };
                currentAdCache.elements[cleanIdWithPaginationNumbers(id)] =
                    adCache;
                currentAdCache.url = getUrlWithoutHash();

                sessionStorage.setItem(
                    AD_CACHE_KEY,
                    JSON.stringify(currentAdCache),
                );
            }
        }
    }
}

export function checkToClear(adCache: AdCache): boolean {
    if (window) {
        if (adCache.url) {
            return (
                adCache.url.length &&
                adCache.url !== getUrlWithoutHash() &&
                !window.location.search.includes('finnkode')
            );
        } else {
            return false;
        }
    }
    return true;
}

export function clearAdPlaceholderCache() {
    if (window) {
        sessionStorage.removeItem(AD_CACHE_KEY);
    }
}
